import { usePatchBillingEntityBo } from "@/api/billingEntity.api";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { GetAllCompanyDto, PatchBillingEntityBoDto } from "@/types/api/api-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type EditableField = {
  [K in keyof PatchBillingEntityBoDto]-?: PatchBillingEntityBoDto[K] extends string | undefined
    ? K
    : never;
}[keyof PatchBillingEntityBoDto];

type EditBillingEntityFieldModalProps = {
  open: boolean;
  onClose: () => void;
  billingEntity?: GetAllCompanyDto["billingEntities"][number];
  field: EditableField;
  title: string;
  label: string;
  placeholder: string;
};

export const EditBillingEntityFieldModal = ({
  open,
  onClose,
  billingEntity,
  field,
  title,
  label,
  placeholder,
}: EditBillingEntityFieldModalProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [value, setValue] = useState("");

  const { mutateAsync: patchBillingEntityBo, isPending } = usePatchBillingEntityBo();

  const initialValue = billingEntity?.[field] ?? "";

  useEffect(() => {
    if (open && billingEntity) {
      setValue(initialValue);
    }
  }, [open, billingEntity, initialValue]);

  const handleSubmit = async () => {
    if (!billingEntity) return;
    await patchBillingEntityBo({
      id: billingEntity.id,
      data: { [field]: value },
    });
    onClose();
  };

  return (
    <Modal
      open={open && !!billingEntity}
      onClose={onClose}
      title={title}
      description={billingEntity?.companyName}
      size="md"
      contentClassName="max-w-[600px]"
      footer={
        <div className="flex w-full gap-2">
          <Button type="button" className="flex-1" onClick={onClose} variant="secondary">
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={handleSubmit}
            variant="default"
            disabled={isPending}
          >
            {t("billingEntityEditModal.submit")}
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-row items-center justify-between py-4">
        <div className="flex flex-1 flex-col">
          <p className="text-sm font-medium text-gray-600">{label}</p>
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-60 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          disabled={isPending}
        />
      </div>
    </Modal>
  );
};
